/**
 * StatusBanner — the roster's headline: is what we are looking at the ward's
 * answer yet?
 *
 * DRAFT. A proposal that has been committed but not published is not the
 * ward's answer, and a banner that did not say so let a lead believe a plan was
 * live. Every draft in view belongs to one plan, which is what makes publishing
 * or discarding it one act.
 *
 * Status is carried by icon + label + text, never colour alone (NHS / WCAG), and
 * the async states are handled here so a cold read never shows a bare colour or
 * a blank bar.
 */
import { useTranslation } from 'react-i18next'
import { AlertTriangle, FileEdit, RefreshCw } from 'lucide-react'

import { Skeleton } from '@/components/ui/skeleton'

type Tone = 'warn' | 'info'

const TONE_STYLE: Record<Tone, { bg: string; fg: string }> = {
  warn: { bg: 'var(--color-tone-warn-tint)', fg: 'var(--color-tone-warn)' },
  info: { bg: 'var(--color-neutral-50)', fg: 'var(--color-surface-mute)' },
}

export interface StatusBannerProps {
  /** How many items the ward's roster plan proposes but nobody has published. */
  proposedCount: number
  shiftsLoading: boolean
  shiftsError: boolean
  shiftsFetching: boolean
  shiftsReady: boolean
  /** The roster drew, but what each cell shows and what was worked did not. */
  occurrencesUnavailable?: boolean
  onRetry: () => void
}

function Shell({ tone, children }: { tone: Tone; children: React.ReactNode }) {
  const style = TONE_STYLE[tone]
  return (
    <div
      role="status"
      className="flex items-center gap-2 px-4 py-2.5 text-[14px] font-medium"
      style={{ backgroundColor: style.bg, color: style.fg }}
    >
      {children}
    </div>
  )
}

export function StatusBanner({
  proposedCount,
  shiftsLoading,
  shiftsError,
  shiftsFetching,
  shiftsReady,
  occurrencesUnavailable = false,
  onRetry,
}: StatusBannerProps) {
  const { t } = useTranslation()

  // The roster READ, so the grid below is real. What did not read is what each
  // cell shows and what was worked — so every chip falls back to its bare code,
  // which is indistinguishable from a plan nobody has published. Said first,
  // because a lead acting on a roster needs to know the grid is incomplete
  // before they read anything off it.
  if (occurrencesUnavailable) {
    return (
      <Shell tone="warn">
        <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span className="min-w-0 flex-1">
          {t(
            'roster.status.occurrencesUnavailable',
            'Shift names and what has been worked could not be read, so cells show their code only',
          )}
        </span>
        <button
          type="button"
          onClick={onRetry}
          className="inline-flex shrink-0 items-center gap-1 rounded-sm font-semibold underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary-500)]"
        >
          <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
          {t('roster.status.retry', 'Retry')}
        </button>
      </Shell>
    )
  }

  // Read error, nothing cached → warn + retry (never a bare colour).
  if (shiftsError && !shiftsReady) {
    return (
      <Shell tone="warn">
        <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span className="min-w-0 flex-1">
          {t('roster.status.rosterError', 'The roster could not be loaded')}
        </span>
        <button
          type="button"
          onClick={onRetry}
          className="inline-flex shrink-0 items-center gap-1 rounded-sm font-semibold underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary-500)]"
        >
          <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
          {t('actions.retry', 'Retry')}
        </button>
      </Shell>
    )
  }

  // Loading, nothing cached → skeleton line (no blank bar).
  if (shiftsLoading && !shiftsReady) {
    return (
      <div className="flex items-center gap-2 px-4 py-2.5" aria-busy="true">
        <Skeleton className="h-4 w-4 rounded-full" />
        <Skeleton className="h-4 w-56" />
      </div>
    )
  }

  if (proposedCount === 0) return null

  return (
    <Shell tone="info">
      <FileEdit className="h-4 w-4 shrink-0" aria-hidden="true" />
      <span className="min-w-0 flex-1">
        {t('roster.status.draft', '{{count}} proposed shift not published yet', {
          count: proposedCount,
        })}
      </span>
      {shiftsFetching && shiftsReady && (
        <span className="shrink-0 text-[12px] font-normal opacity-70">
          {t('roster.status.updating', 'updating…')}
        </span>
      )}
    </Shell>
  )
}
